import { useFullscreenPortal } from './chunk-MBTLYAMU.js';
import { cn } from './chunk-FFX3LZW7.js';
import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { jsx, jsxs } from 'react/jsx-runtime';

// src/primitives/sizing.ts
var CONTROL_WIDTHS = { full: "w-full", sm: "w-40 max-w-full", md: "w-44 max-w-full", lg: "w-48 max-w-full", xl: "w-56 max-w-full" };
var DIALOG_WIDTHS = { sm: "max-w-sm", md: "max-w-md", lg: "max-w-lg", xl: "max-w-xl", "2xl": "max-w-2xl", "3xl": "max-w-3xl", "4xl": "max-w-4xl", "5xl": "max-w-5xl", "6xl": "max-w-6xl" };
var SHEET_WIDTHS = { default: "", navigation: "w-72 max-w-[85vw]", panel: "w-full sm:max-w-md", wide: "w-full sm:max-w-lg" };
var Dialog = DialogPrimitive.Root;
var DialogTrigger = DialogPrimitive.Trigger;
var DialogClose = DialogPrimitive.Close;
function DialogPortal({ container, ...props }) {
  const fullscreen = useFullscreenPortal();
  return /* @__PURE__ */ jsx(DialogPrimitive.Portal, { container: container === void 0 ? fullscreen : container, ...props });
}
var DialogOverlay = React.forwardRef(function DialogOverlay2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    DialogPrimitive.Overlay,
    {
      ref,
      className: cn(
        "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      ),
      ...props
    }
  );
});
var DialogContent = React.forwardRef(function DialogContent2({ className, children, showClose = true, size = "lg", ...props }, ref) {
  return /* @__PURE__ */ jsxs(DialogPortal, { children: [
    /* @__PURE__ */ jsx(DialogOverlay, {}),
    /* @__PURE__ */ jsxs(
      DialogPrimitive.Content,
      {
        ref,
        className: cn(
          // `grid-cols-[minmax(0,1fr)]`, not the implicit `auto` column: an
          // auto track sizes to its widest child's min-content, so one
          // `truncate` line (a build error, a long id) widened the grid past
          // the dialog and pushed row actions and the footer off-screen.
          // Never taller than the window: centred by translate, a taller dialog
          // lost its header AND its footer off both edges with nothing to
          // scroll — a 100-chain registry review (1,328px in a 964px window)
          // could not be approved (2026-10-01). A caller's own max-h/overflow
          // still wins.
          "fixed left-[50%] top-[50%] z-50 grid max-h-[calc(100dvh-2rem)] w-full max-w-lg grid-cols-[minmax(0,1fr)] translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out sm:rounded-lg",
          DIALOG_WIDTHS[size],
          className
        ),
        ...props,
        children: [
          children,
          showClose && /* @__PURE__ */ jsxs(DialogPrimitive.Close, { className: "absolute right-3 top-3 flex size-6 items-center justify-center rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-ring", children: [
            /* @__PURE__ */ jsx(X, { className: "size-4" }),
            /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Close" })
          ] })
        ]
      }
    )
  ] });
});
function DialogHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-col gap-y-1.5 text-center sm:text-left", className), ...props });
}
function DialogFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className), ...props });
}
var DialogTitle = React.forwardRef(function DialogTitle2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    DialogPrimitive.Title,
    {
      ref,
      className: cn("text-lg font-semibold leading-none tracking-tight", className),
      ...props
    }
  );
});
var DialogDescription = React.forwardRef(function DialogDescription2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(DialogPrimitive.Description, { ref, className: cn("text-sm text-muted-foreground", className), ...props });
});

export { CONTROL_WIDTHS, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, SHEET_WIDTHS };
//# sourceMappingURL=chunk-JY5DSE5Q.js.map
//# sourceMappingURL=chunk-JY5DSE5Q.js.map